"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Card,
  CardBody,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Progress,
} from "@heroui/react";
import { useEffect, useState } from "react";
import { type Control, Controller, useForm, useWatch } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { useMediaQuery } from "usehooks-ts";
import {
  Building2,
  CheckCircle2,
  Globe,
  Mail,
  MapPin,
  Phone,
  User,
} from "lucide-react";
import { Form } from "../../components/ui/form";
import { RegisterFarm } from "../Animals/animalApiSlice";
import useAuth from "../../hooks/useAuth";
import { farmSchema, type TFarm } from "@/models/Farm.Schema.ts";
import { AuthStore } from "@/store/useAuthStore.ts";
import { farmType } from "@/utils/items.ts";
import logo from "/public/logo.png";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";




const trackedFields: (keyof TFarm)[] = [
  "farmName",
  "farmType",
  "ownerName",
  "phoneNumber",
  "email",
  "status",
  "country",
  "state",
  "city",
];

function FarmRegistrationProgress({ control }: { control: Control<TFarm> }) {
  const watched = useWatch({ control });
  const completedFields = trackedFields.filter((key) => {
    const val = watched[key];
    return typeof val === "string" && val.trim().length > 0;
  }).length;
  const totalFields = trackedFields.length;
  const progressPercentage = Math.min(
    100,
    Math.round((completedFields / totalFields) * 100)
  );

  return (
    <Progress
      value={progressPercentage}
      className="w-full"
      color="primary"
      size="sm"
      classNames={{
        indicator: "bg-gradient-to-r from-blue-300 to-green-300",
      }}
    />
  );
}

export default function FarmForm() {
  const [latitude, setLatitude] = useState<number | undefined>();
  const [longitude, setLongitude] = useState<number | undefined>();
  const isMobile = useMediaQuery("(max-width: 768px)");

  useEffect(() => {
    if (typeof navigator !== "undefined" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          if (lat !== null && lng !== null) {
            setLatitude(lat);
            setLongitude(lng);
          }
        },
        (error) => {
          console.warn("Geolocation permission denied or unavailable:", error.message);
        },
        { timeout: 10000 }
      );
    }
  }, []);

  const form = useForm<TFarm>({
    resolver: zodResolver(farmSchema),
    mode: "onTouched",
    defaultValues: {
      farmName: "",
      farmType: "",
      ownerName: "",
      phoneNumber: "",
      email: "",
      status: "active",
      country: "",
      state: "",
      city: "",
      zipCode: "",
      address: "",
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = form;

  const queryClient = useQueryClient();
  const auth = useAuth();
  const id = auth?.id || auth?.UserInfo?.id;
  const { setFarmInfo, updateCredentials } = AuthStore();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ["farms", "credentials"],
    mutationFn: async (data: any) => {
      return await RegisterFarm(data);
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["farms", "credentials"] });
      setFarmInfo({
        id: data._id,
        farmOwnerId: data.farmOwnerId,
        farmName: data.farmName,
        phoneNumber: data.phoneNumber,
        farmType: data.farmType,
        country: data.country,
        state: data.state,
        city: data.city,
        address: data.address || data.zipCode,
        ownerName: data.ownerName,
        email: data.email,
        locations: data.locations,
        approvedAnimalRegistrations: data.approvedAnimalRegistrations || [],
        rejectedAnimalRegistrations: data.rejectedAnimalRegistrations || [],
        registeredAnimals: data.registeredAnimals || [],
        monitoredAnimals: data.monitoredAnimals || [],
        status: data.status,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        lastLogin: data.lastLogin,
        assignedSupervisor: data.assignedSupervisor,
      });
      updateCredentials({ ownFarm: true, farmName: data.farmName });
      // Modal closes automatically — Main.tsx reads ownFarm from store
      reset();
    },
    onError: (error: any) => {
      if (error instanceof AxiosError && error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to register farm. Please verify your details.");
      }
    },
  });

  const onSubmit = async (values: TFarm) => {
    if (!id) {
      toast.error("User session missing. Please re-login to register your farm.");
      return;
    }

    const payload = {
      ...values,
      farmer: id,
      address: values.address || values.zipCode,
      latitude: latitude ?? 0,
      longitude: longitude ?? 0,
    };

    try {
      await mutateAsync(payload);
      toast.success("Successfully Registered Your Farm!");
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  return (
    <Modal
      isOpen={true}
      isDismissable={false}
      hideCloseButton={true}
      disableAnimation={true}
      className="bg-gradient-to-br from-slate-50 to-yellow-50/30"
      placement="center"
      scrollBehavior="inside"
      size={isMobile ? "full" : "5xl"}
    >
      <ModalContent className="bg-gradient-to-br from-white/95 to-yellow-50/80 backdrop-blur-xl border border-yellow-200/30">
        <ModalHeader className="flex flex-col gap-4 pb-2 bg-gradient-to-r from-white/60 to-transparent backdrop-blur-sm border-b border-yellow-200/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-gradient-to-r from-yellow-100 to-green-100 rounded-full border border-yellow-200/50">
                <img alt="Logo" className="h-6 z-20" src={logo} />
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-slate-700 to-green-700 bg-clip-text text-transparent">
                  Register Your Farm
                </h2>
                <p className="text-slate-600 text-sm">
                  Complete your farm registration to get started
                </p>
              </div>
            </div>
          </div>
          <FarmRegistrationProgress control={control} />
        </ModalHeader>

            <ModalBody className="p-8">
              <Form {...form}>
                <form
                  id="farm-registration-form"
                  className="space-y-8"
                  onSubmit={handleSubmit(onSubmit)}
                >
                  {/* Farm Details Section */}
                  <Card className="bg-gradient-to-br from-blue-50/60 to-blue-100/40 backdrop-blur-sm border border-blue-200/40 hover:border-blue-300/60 transition-all duration-300">
                    <CardBody className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-r from-blue-100 to-blue-200 rounded-lg border border-blue-300/50">
                          <Building2 className="w-5 h-5 text-blue-700" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800">
                          Farm Details
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Controller
                          control={control}
                          name="farmName"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="Farm Name"
                              labelPlacement="outside"
                              placeholder="Enter your farm name"
                              size="lg"
                              variant="bordered"
                              isInvalid={Boolean(errors.farmName)}
                              errorMessage={errors.farmName?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-blue-200/50 hover:border-blue-300/70 transition-colors",
                              }}
                              startContent={
                                <Building2 className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="farmType"
                          render={({ field }) => (
                            <div className="flex flex-col">
                              <label
                                htmlFor="farmType"
                                className="text-sm font-medium text-slate-700 mb-2"
                              >
                                Farm Type
                              </label>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger
                                  id="farmType"
                                  className={`h-12 rounded-lg bg-white/60 border text-slate-800 hover:border-blue-300/70 transition-colors ${
                                    errors.farmType
                                      ? "border-rose-500 ring-1 ring-rose-500"
                                      : "border-blue-200/50"
                                  }`}
                                >
                                  <SelectValue placeholder="Select farm type" />
                                </SelectTrigger>
                                <SelectContent
                                  className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white"
                                  position="popper"
                                >
                                  {farmType.map(
                                    (farm: { value: string; label: string }) => (
                                      <SelectItem
                                        key={farm.value}
                                        value={farm.value}
                                      >
                                        {farm.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              {errors.farmType && (
                                <p className="text-xs text-rose-500 mt-1">
                                  {errors.farmType.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>

                  {/* Owner Information Section */}
                  <Card className="bg-gradient-to-br from-green-50/60 to-green-100/40 backdrop-blur-sm border border-green-200/40 hover:border-green-300/60 transition-all duration-300">
                    <CardBody className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-r from-green-100 to-green-200 rounded-lg border border-green-300/50">
                          <User className="w-5 h-5 text-green-700" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800">
                          Owner Information
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Controller
                          control={control}
                          name="ownerName"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="Owner Name"
                              labelPlacement="outside"
                              placeholder="Enter owner's full name"
                              size="lg"
                              variant="bordered"
                              isInvalid={Boolean(errors.ownerName)}
                              errorMessage={errors.ownerName?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-green-200/50 hover:border-green-300/70 transition-colors",
                              }}
                              startContent={
                                <User className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="email"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="Email Address"
                              labelPlacement="outside"
                              placeholder="Enter email address"
                              size="lg"
                              type="email"
                              variant="bordered"
                              isInvalid={Boolean(errors.email)}
                              errorMessage={errors.email?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-green-200/50 hover:border-green-300/70 transition-colors",
                              }}
                              startContent={
                                <Mail className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>

                  {/* Contact & Status Section */}
                  <Card className="bg-gradient-to-br from-yellow-50/60 to-yellow-100/40 backdrop-blur-sm border border-yellow-200/40 hover:border-yellow-300/60 transition-all duration-300">
                    <CardBody className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-lg border border-yellow-300/50">
                          <Phone className="w-5 h-5 text-yellow-700" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800">
                          Contact & Status
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                        <Controller
                          control={control}
                          name="phoneNumber"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="Phone Number"
                              labelPlacement="outside"
                              placeholder="e.g. 0912345678"
                              size="lg"
                              type="tel"
                              variant="bordered"
                              isInvalid={Boolean(errors.phoneNumber)}
                              errorMessage={errors.phoneNumber?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-yellow-200/50 hover:border-yellow-300/70 transition-colors",
                              }}
                              startContent={
                                <Phone className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="status"
                          render={({ field }) => (
                            <div className="flex flex-col">
                              <label
                                htmlFor="farmStatus"
                                className="text-sm font-medium text-slate-700 mb-2"
                              >
                                Farm Status
                              </label>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger
                                  id="farmStatus"
                                  className={`h-12 rounded-lg bg-white/60 border text-slate-800 hover:border-yellow-300/70 transition-colors ${
                                    errors.status
                                      ? "border-rose-500 ring-1 ring-rose-500"
                                      : "border-yellow-200/50"
                                  }`}
                                >
                                  <SelectValue placeholder="Select farm status" />
                                </SelectTrigger>
                                <SelectContent
                                  className="max-h-[300px] overflow-y-auto z-50 shadow-lg bg-white"
                                  position="popper"
                                >
                                  <SelectItem value="active">Active</SelectItem>
                                  <SelectItem value="inactive">Inactive</SelectItem>
                                  <SelectItem value="starting soon">
                                    Starting Soon
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {errors.status && (
                                <p className="text-xs text-rose-500 mt-1">
                                  {errors.status.message}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>

                  {/* Location Section */}
                  <Card className="bg-gradient-to-br from-blue-50/60 to-green-50/40 backdrop-blur-sm border border-blue-200/40 hover:border-green-300/60 transition-all duration-300">
                    <CardBody className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-gradient-to-r from-blue-100 to-green-100 rounded-lg border border-blue-300/50">
                          <MapPin className="w-5 h-5 text-blue-700" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800">
                          Location Details
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Controller
                          control={control}
                          name="country"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="Country"
                              labelPlacement="outside"
                              placeholder="Enter country"
                              size="lg"
                              variant="bordered"
                              isInvalid={Boolean(errors.country)}
                              errorMessage={errors.country?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-blue-200/50 hover:border-blue-300/70 transition-colors",
                              }}
                              startContent={
                                <Globe className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="state"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="State / Region"
                              labelPlacement="outside"
                              placeholder="Enter state or region"
                              size="lg"
                              variant="bordered"
                              isInvalid={Boolean(errors.state)}
                              errorMessage={errors.state?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-green-200/50 hover:border-green-300/70 transition-colors",
                              }}
                              startContent={
                                <MapPin className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="city"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="City"
                              labelPlacement="outside"
                              placeholder="Enter city"
                              size="lg"
                              variant="bordered"
                              isInvalid={Boolean(errors.city)}
                              errorMessage={errors.city?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-blue-200/50 hover:border-blue-300/70 transition-colors",
                              }}
                              startContent={
                                <MapPin className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                        <Controller
                          control={control}
                          name="zipCode"
                          render={({ field }) => (
                            <Input
                              {...field}
                              label="Zip / Postal Code (Optional)"
                              labelPlacement="outside"
                              placeholder="Enter zip code (optional)"
                              size="lg"
                              variant="bordered"
                              isInvalid={Boolean(errors.zipCode)}
                              errorMessage={errors.zipCode?.message}
                              classNames={{
                                input: "text-slate-800",
                                label: "text-slate-700 font-medium",
                                inputWrapper:
                                  "bg-white/60 border-green-200/50 hover:border-green-300/70 transition-colors",
                              }}
                              startContent={
                                <MapPin className="w-4 h-4 text-slate-500" />
                              }
                            />
                          )}
                        />
                      </div>
                    </CardBody>
                  </Card>
                </form>
              </Form>
            </ModalBody>

            <ModalFooter className="bg-gradient-to-r from-white/60 to-transparent backdrop-blur-sm border-t border-yellow-200/30">
              <div className="flex justify-between items-center w-full">
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <CheckCircle2
                    className={`w-4 h-4 ${
                      latitude !== undefined ? "text-green-600" : "text-amber-500"
                    }`}
                  />
                  <span>
                    {latitude !== undefined
                      ? "Auto-location active"
                      : "Location coordinates optional"}
                  </span>
                </div>
                <Button
                  form="farm-registration-form"
                  type="submit"
                  color="primary"
                  size="lg"
                  isLoading={isPending}
                  className="bg-gradient-to-r from-blue-200 to-green-200 hover:from-blue-300 hover:to-green-300 text-slate-800 transition-all duration-300 hover:scale-105 border border-blue-300/50 font-semibold"
                >
                  {isPending ? "Registering Farm..." : "Register Farm"}
                </Button>
              </div>
            </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
